import React, { useRef, useState } from 'react';
import Dropzone, {
    IDropzoneProps,
    ILayoutProps,
} from "react-dropzone-uploader";
import { useAppDispatch } from "../../redux";
import { addedAttachment } from "../../redux";
import { Attachment } from '../../models';
import { AttachmentsList } from '../AttachmentsList';
import { Alert, Icon } from '..';
import { t } from 'i18next';

interface UploaderProps {
    projectId: string;
    isBugReport?: boolean;
    changeId?: string;
    showChangeSlug?: boolean;
    hideFiles?: boolean;
    onUpload?: (attachment: Attachment) => void;
}

const ACTIVE_UPLOAD_STATUSES = new Set([
    'preparing',
    'getting_upload_params',
    'uploading',
]);

const FINISHED_UPLOAD_STATUSES = new Set([
    'done',
    'error_upload',
    'error_upload_params',
    'aborted',
    'removed',
]);

// add type defs to custom LayoutComponent prop to easily inspect props passed to injected components
const Layout = ({ dropzoneProps, input }: ILayoutProps) => {
    dropzoneProps.className += ' relative my-2 text-center border-2 border-gray-300 dark:border-gray-600 border-dotted p-4 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:bg-gray-800';

    return (
        <div {...dropzoneProps}>
            {input}
        </div>
    );
};

const renderInputContent = (files: { length: number }[]) => (
    <span className="inline-flex items-center gap-2">
        <Icon type="file" size={4} />
        {files.length
            ? t('attachments.add_files')
            : t('attachments.drag_files')}
    </span>
);

const inputLabelStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    margin: 0,
    width: '100%',
    height: '100%',
};

const Uploader = (props: UploaderProps) => {
    const { projectId, isBugReport, changeId, showChangeSlug, hideFiles, onUpload } = props;

    const [ uploadingCount, setUploadingCount ] = useState(0);
    const [ rejectedType, setRejectedType ] = useState(false);
    const inFlightIdsRef = useRef<Set<string>>(new Set());

    const dispatch = useAppDispatch();

    const getUploadParams: IDropzoneProps["getUploadParams"] = () => {
        let url;
        if (isBugReport) {
            url = Attachment.createBugReportAttachmentUrl(changeId ?? "");
        } else if (changeId) {
            url = Attachment.createChangeAttachmentUrl(changeId);
        } else {
            url = Attachment.createProjectAttachmentUrl(projectId);
        }

        return { url };
    };

    const handleStatus: IDropzoneProps['onChangeStatus'] = (fileWithMeta: AnyObj, status) => {
        const fileId = String(fileWithMeta?.meta?.id ?? '');

        if (status === 'rejected_file_type') {
            setRejectedType(true);
            return;
        }

        if (ACTIVE_UPLOAD_STATUSES.has(status) && fileId && !inFlightIdsRef.current.has(fileId)) {
            setRejectedType(false);
            inFlightIdsRef.current.add(fileId);
            setUploadingCount((count) => count + 1);
            return;
        }

        if (FINISHED_UPLOAD_STATUSES.has(status) && fileId && inFlightIdsRef.current.has(fileId)) {
            inFlightIdsRef.current.delete(fileId);
            setUploadingCount((count) => Math.max(0, count - 1));
        }

        if (status === 'done') {
            const response = JSON.parse(fileWithMeta.xhr.response);
            const newAttachment = new Attachment(response.attachment);
            dispatch(addedAttachment(newAttachment));
            if (onUpload)
                onUpload(newAttachment);
        }
    };

    return (
        <>
            {rejectedType && (
                <Alert color='failure' title={t('attachments.unauthorized_file')} icon='warning'/>
            )}
            <div className="h-20 flex justify-center items-center relative">
                <Dropzone
                    getUploadParams={getUploadParams}
                    onChangeStatus={handleStatus}
                    LayoutComponent={Layout}
                    accept={Attachment.acceptedFiles()}
                    inputContent={renderInputContent}
                    inputWithFilesContent={renderInputContent}
                    styles={{
                        input: { display: 'none' },
                        inputLabel: inputLabelStyle,
                        inputLabelWithFiles: inputLabelStyle,
                        dropzone: { width: '100%', height: '4rem' },
                    }}
                />
                {uploadingCount > 0 && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-white/60 dark:bg-gray-900/60">
                        <Icon type="loading" size={8} />
                    </div>
                )}
            </div>
            {!hideFiles && (
                <AttachmentsList projectId={projectId} changeId={changeId} showChangeSlug={showChangeSlug} />
            )}
        </>
    );
};

export default Uploader;
